/**
 * useBlogPosts – Immozwerg Blog (automatisch aus Google Sheets)
 * 
 * Google Sheets Spalten (A–J):
 *   A(0): Datum der Veröffentlichung
 *   B(1): Thema (Titel)
 *   C(2): Kategorie
 *   D(3): Freigabe von Andrea ("go" = freigegeben)
 *   E(4): Inhalt / Notizen (Teaser)
 *   F(5): Keywords (SEO)
 *   G(6): Freigabe (Entwurf) ("go" = Entwurf fertig)
 *   H(7): Titelbild
 *   I(8): Visueller Entwurf Markdown (Link) ← **MARKDOWN-URL**
 *   J(9): Online-Status ("go" = online)
 */
import { useState, useEffect } from "react";
import { useSSRData } from "@/contexts/SSRDataContext";
import { dedupeBlogPostsBySlug, sortBlogPostsNewestFirst } from "@shared/blog-sorting";
import { normalizeBlogContentUrl } from "@shared/blog-content-url";

const SHEET_CSV_URL =
  "https://docs.google.com/spreadsheets/d/1k4mQ7aajVpIsZOxRMUj9tZWcA-DhDQbE03IOm8KWwj0/export?format=csv&gid=0";

export interface BlogPost {
  slug: string;
  datum: string;
  titel: string;
  kategorie: string;
  teaser: string;
  keywords: string;
  bild: string;
  markdownUrl: string;
}

/** Titel → URL-freundlicher Slug */
export function titelZuSlug(titel: string): string {
  return titel
    .toLowerCase()
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");
}

/** Einfacher CSV-Parser (berücksichtigt quoted fields mit Kommas) */
function parseCSV(text: string): string[][] {
  const rows: string[][] = [];
  const lines = text.split(/\r?\n/);
  for (const line of lines) {
    if (!line.trim()) continue;
    const cols: string[] = [];
    let cur = "";
    let inQuote = false;
    for (let i = 0; i < line.length; i++) {
      const ch = line[i];
      if (ch === '"') {
        if (inQuote && line[i + 1] === '"') { 
          cur += '"'; 
          i++; 
        } else { 
          inQuote = !inQuote; 
        }
      } else if (ch === "," && !inQuote) {
        cols.push(cur); 
        cur = "";
      } else {
        cur += ch;
      }
    }
    cols.push(cur);
    rows.push(cols);
  }
  return rows;
}

/** Google Drive URL konvertieren zu usercontent (wie bei Immobilien) */
function convertDriveUrl(url: string): string {
  if (!url) return "";
  // Google Drive URLs → server-side proxy (bypasses CORS)
  const match = url.match(/[?&]id=([^&]+)/);
  if (match) {
    return `/api/drive-image?id=${match[1]}`;
  }
  const match2 = url.match(/\/file\/d\/([^/]+)\//);
  if (match2) {
    return `/api/drive-image?id=${match2[1]}`;
  }
  return url;
}

export function useBlogPosts() {
  const ssrData = useSSRData();
  
  // Wenn SSR-Daten vorhanden sind, direkt verwenden (kein Fetch nötig)
  const hasSSRData = !!(ssrData?.blogPosts && ssrData.blogPosts.length > 0);
  
  const [posts, setPosts] = useState<BlogPost[]>(() => {
    if (hasSSRData) {
      // SSR-Daten haben kein konvertiertes Bild-URL – hier nachholen
      return dedupeBlogPostsBySlug(sortBlogPostsNewestFirst(ssrData!.blogPosts!.map(p => ({
        ...p,
        bild: convertDriveUrl(p.bild || ""),
      }))));
    }
    return [];
  });
  const [loading, setLoading] = useState(!hasSSRData);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    // SSR liefert den ersten sichtbaren Stand. Der nachfolgende Abruf hält
    // neue freigegebene Beiträge ohne Neu-Deploy und ohne Cache-Wartezeit aktuell.
    if (!hasSSRData) setLoading(true);
    fetch(SHEET_CSV_URL)
      .then((r) => {
        if (!r.ok) throw new Error("Sheet nicht erreichbar");
        return r.text();
      })
      .then((text) => {
        if (cancelled) return;
        const rows = parseCSV(text);
        // Erste Zeile = Header, überspringen
        const data = rows.slice(1);
        const result: BlogPost[] = [];
        
        for (const row of data) {
          // Spalte D (Index 3): Freigabe von Andrea
          const freigabeAndrea = (row[3] ?? "").trim().toLowerCase();
          // Spalte G (Index 6): Freigabe (Entwurf)
          const freigabeEntwurf = (row[6] ?? "").trim().toLowerCase();
          
          // Nur wenn beide Freigaben = "go"
          if (freigabeAndrea !== "go" || freigabeEntwurf !== "go") continue;
          
          const titel = (row[1] ?? "").trim();
          if (!titel) continue;
          
          // Spalte I (Index 8): Markdown-URL
          const markdownUrl = normalizeBlogContentUrl((row[8] ?? "").trim());
          if (!markdownUrl) continue;
          
          // Spalte H (Index 7): Titelbild – konvertiere Google Drive URLs
          const bildUrl = (row[7] ?? "").trim();
          
          result.push({
            slug: titelZuSlug(titel),
            datum: (row[0] ?? "").trim(),
            titel,
            kategorie: (row[2] ?? "").trim(),
            teaser: (row[4] ?? "").trim(),
            keywords: (row[5] ?? "").trim(),
            bild: convertDriveUrl(bildUrl),
            markdownUrl,
          });
        }
        
        setPosts(dedupeBlogPostsBySlug(sortBlogPostsNewestFirst(result)));
        setLoading(false);
      })
      .catch((e) => {
        if (!cancelled) {
          // Bei einem temporären Sheets-Fehler bleibt der vorhandene SSR-Stand
          // sichtbar, statt die aktuelle Blogübersicht zu ersetzen.
          if (!hasSSRData) setError(e.message);
          setLoading(false);
        }
      });
    
    return () => { cancelled = true; };
  }, [hasSSRData]);

  return { posts, loading, error };

}
